// The HTTP contract between the dashboard and the Worker (01 §6). One zod schema
// per request and response; the Worker validates with them and the web app
// imports the inferred types, so a changed route fails the build on both ends.
//
// Conventions: ids are strings, times are epoch milliseconds, every mutating
// request carries an `Idempotency-Key` header (UUIDv7, one per user intent),
// and mutations on versioned resources carry `If-Match: "v<version>"`.

import { z } from 'zod'
import { ChatSwitches, TicketingSwitches } from './project-file.js'

/** Mirrors PROJECT_STAGES in @one-deployment/shared (this package has no dependencies but zod). */
export const PROJECT_STAGES = ['foundations', 'detailed_scope', 'mvp', 'backend', 'final_feedback', 'production', 'support'] as const

// ── Shared ──────────────────────────────────────────────────────────────────

export const Role = z.enum(['viewer', 'dev', 'lead', 'admin'])
export type Role = z.infer<typeof Role>
export const ProjectRole = z.enum(['viewer', 'dev', 'lead'])
export type ProjectRole = z.infer<typeof ProjectRole>

export const Stage = z.enum([
  'queued', 'triage', 'triaged', 'declined', 'implementing', 'verifying', 'proving',
  'pr_open', 'pr_pending', 'paused', 'needs_human', 'merged', 'dropped', 'done_elsewhere',
  'testing', 'verified', 'rejected',
])
export type Stage = z.infer<typeof Stage>

export const JobMode = z.enum(['build', 'fix', 'verify', 'screen', 'reply', 'classify', 'split'])
export type JobMode = z.infer<typeof JobMode>
export const JobStatus = z.enum(['queued', 'leased', 'done', 'failed', 'cancelled', 'gave_up'])
export type JobStatus = z.infer<typeof JobStatus>
export const JobSource = z.enum(['delegate', 'webhook', 'sweep', 'manual', 'mention'])
export type JobSource = z.infer<typeof JobSource>

/** Every error body. `code` is stable; `message` is a sentence for a person. */
export const ApiError = z.object({
  error: z.object({
    code: z.enum([
      'bad_request', 'unauthorized', 'mfa_required', 'forbidden', 'not_found', 'conflict',
      'in_progress', 'idempotency_key_reused', 'idempotency_key_required', 'precondition_required',
      'rate_limited', 'lease_lost', 'unavailable', 'internal',
    ]),
    message: z.string(),
    /** For `conflict`: the current row, so the UI can show what changed. */
    current: z.unknown().optional(),
  }),
})
export type ApiError = z.infer<typeof ApiError>

export const Ok = z.object({ ok: z.literal(true) })
export type Ok = z.infer<typeof Ok>

const Versioned = { version: z.number().int() }

// ── Auth ────────────────────────────────────────────────────────────────────

export const MfaFactor = z.enum(['totp', 'passkey'])
export type MfaFactor = z.infer<typeof MfaFactor>

export const LoginRequest = z.object({ email: z.string().email(), password: z.string().min(1).max(1024) })
export type LoginRequest = z.infer<typeof LoginRequest>

/**
 * A login never yields a full session directly: 2FA is compulsory. `next` says
 * which screen comes next. `enroll` means no factor yet (a new account).
 */
export const LoginResponse = z.object({
  next: z.enum(['totp', 'passkey', 'enroll']),
})
export type LoginResponse = z.infer<typeof LoginResponse>

export const TotpCode = z.object({ code: z.string().regex(/^\d{6}$/) })
export type TotpCode = z.infer<typeof TotpCode>

export const RecoveryCodeRequest = z.object({ code: z.string().min(8).max(32) })
export type RecoveryCodeRequest = z.infer<typeof RecoveryCodeRequest>

export const TotpEnrollResponse = z.object({ secret: z.string(), otpauthUrl: z.string() })
export type TotpEnrollResponse = z.infer<typeof TotpEnrollResponse>

/** Returned when the first factor is confirmed: shown once, never again. */
export const EnrollConfirmResponse = z.object({ recoveryCodes: z.array(z.string()).nullable() })
export type EnrollConfirmResponse = z.infer<typeof EnrollConfirmResponse>

/** WebAuthn options/responses are passed through as JSON (simplewebauthn shapes). */
export const PasskeyOptionsResponse = z.object({ challengeId: z.string(), options: z.record(z.unknown()) })
export type PasskeyOptionsResponse = z.infer<typeof PasskeyOptionsResponse>
export const PasskeyVerifyRequest = z.object({
  challengeId: z.string(),
  response: z.record(z.unknown()),
  label: z.string().min(1).max(64).optional(),
})
export type PasskeyVerifyRequest = z.infer<typeof PasskeyVerifyRequest>

export const ForgotRequest = z.object({ email: z.string().email() })
export type ForgotRequest = z.infer<typeof ForgotRequest>
export const ResetRequest = z.object({ token: z.string().min(16), password: z.string().min(12).max(1024) })
export type ResetRequest = z.infer<typeof ResetRequest>
export const InviteAcceptRequest = z.object({
  token: z.string().min(16),
  name: z.string().min(1).max(100),
  password: z.string().min(12).max(1024),
})
export type InviteAcceptRequest = z.infer<typeof InviteAcceptRequest>

export const ProjectGrant = z.object({ projectId: z.string(), key: z.string(), name: z.string(), role: Role })
export type ProjectGrant = z.infer<typeof ProjectGrant>

export const Me = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  globalRole: Role,
  /** `password` = 2FA still pending; only /auth/2fa/* is reachable. */
  stage: z.enum(['password', 'full']),
  factors: z.object({ totp: z.boolean(), passkeys: z.number().int(), recoveryCodesLeft: z.number().int() }),
  projects: z.array(ProjectGrant),
  /** The linked Linear user's picture, for the account button. */
  avatar: z.string().nullable().optional(),
})
export type Me = z.infer<typeof Me>

// ── Account ─────────────────────────────────────────────────────────────────

export const Passkey = z.object({ id: z.string(), label: z.string(), createdAt: z.number(), lastUsedAt: z.number().nullable() })
export type Passkey = z.infer<typeof Passkey>

export const Account = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  globalRole: Role,
  totp: z.boolean(),
  /** When the authenticator app was confirmed, and last accepted (from its 30s step). */
  totpAddedAt: z.number().nullable().optional(),
  totpLastUsedAt: z.number().nullable().optional(),
  passkeys: z.array(Passkey),
  recoveryCodesLeft: z.number().int(),
  ...Versioned,
})
export type Account = z.infer<typeof Account>

export const AccountPatch = z.object({ name: z.string().min(1).max(100) })
export type AccountPatch = z.infer<typeof AccountPatch>
export const PasswordChange = z.object({ current: z.string(), next: z.string().min(12).max(1024) })
export type PasswordChange = z.infer<typeof PasswordChange>
export const RecoveryCodesResponse = z.object({ recoveryCodes: z.array(z.string()) })
export type RecoveryCodesResponse = z.infer<typeof RecoveryCodesResponse>

export const SessionInfo = z.object({
  id: z.string(),
  current: z.boolean(),
  createdAt: z.number(),
  lastSeenAt: z.number(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
})
export type SessionInfo = z.infer<typeof SessionInfo>
export const SessionList = z.object({ sessions: z.array(SessionInfo) })
export type SessionList = z.infer<typeof SessionList>

// ── People ──────────────────────────────────────────────────────────────────

export const Person = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  globalRole: Role,
  disabled: z.boolean(),
  mfaEnrolled: z.boolean(),
  lastLoginAt: z.number().nullable(),
  /** Set by the person on their account page; names them on PRs and merges. */
  githubLogin: z.string().nullable(),
  /** Their Linear picture, when a Linear user is linked to them. */
  avatar: z.string().nullable().optional(),
  createdAt: z.number().optional(),
  projects: z.array(z.object({ projectId: z.string(), key: z.string(), role: ProjectRole })),
  ...Versioned,
})
export type Person = z.infer<typeof Person>
export const PeopleList = z.object({ people: z.array(Person) })
export type PeopleList = z.infer<typeof PeopleList>

export const InviteRequest = z.object({
  email: z.string().email(),
  globalRole: Role.default('dev'),
  projects: z.array(z.object({ projectId: z.string(), role: ProjectRole })).default([]),
})
export type InviteRequest = z.infer<typeof InviteRequest>

export const PersonPatch = z.object({ globalRole: Role.optional(), disabled: z.boolean().optional() })
export type PersonPatch = z.infer<typeof PersonPatch>

export const AccessPut = z.object({ role: ProjectRole })
export type AccessPut = z.infer<typeof AccessPut>

/** A pending invite. `id` is the stored token hash: it names the invite and cannot be accepted with. */
export const Invite = z.object({ id: z.string(), email: z.string(), globalRole: Role, invitedBy: z.string(), expiresAt: z.number() })
export type Invite = z.infer<typeof Invite>
export const InviteList = z.object({ invites: z.array(Invite) })
export type InviteList = z.infer<typeof InviteList>

// ── Projects ────────────────────────────────────────────────────────────────

/** The auto-start rule a project starts with. */
export const DEFAULT_AUTO_START_RULE = 'Start by itself when the ticket is small (S), in scope, and not Money, Data, Access or Security.'

export const ProjectSettings = z.object({
  /** CI/CD from .trisof/ci.yml (features/21). Off: the project keeps GitHub Actions and its file is never read. */
  ci: z.object({
    enabled: z.boolean(),
    /** CI jobs of this project running at once; default 2. */
    parallel: z.number().int().min(1).max(20).optional(),
  }).optional(),
  devCmd: z.string().optional(),
  /** Installs the dependencies in a fresh worktree; from the project file's commands.install. */
  installCmd: z.string().optional(),
  devPort: z.number().int().optional(),
  /** The project file's dev.users, without passwords: the Preview's "Sign in as" choices. */
  devUsers: z.array(z.object({ email: z.string(), label: z.string().optional() })).optional(),
  testCmd: z.string().optional(),
  /** Always run on a build: build, lint, typecheck and the full unit tests. Unset: testCmd runs everything, as before. */
  unitCmd: z.string().optional(),
  /** End-to-end tests; the related spec paths are appended. Run only for flow and dependency levels. */
  e2eCmd: z.string().optional(),
  uatUrl: z.string().url().optional(),
  /** The live app's address: feedback from it is taken too (features/14). */
  prodUrl: z.string().url().optional(),
  triageModel: z.string().optional(),
  implementModel: z.string().optional(),
  containerMemMb: z.number().int().positive().optional(),
  /** CPUs per container; default 2. `general` runs on 1. */
  containerCpus: z.number().int().min(1).max(32).optional(),
  /** What a heavy step (suite, dev server, install) is given before the box has
   *  measured it, MB. Learned peaks replace it after the first run (02 §5.1). */
  memDefaultMb: z.number().int().min(512).max(65536).optional(),
  maxContainers: z.number().int().positive().optional(),
  sessionsPerContainer: z.number().int().positive().optional(),
  image: z.string().optional(),
  /** Token mode: the GitHub account the personal access token belongs to (display and audit only). */
  githubUser: z.string().max(100).optional(),
  /** Linear state *name* that, when a ticket moves into it, starts a build. Unset: only delegation starts work. */
  linearTriggerState: z.string().min(1).max(100).optional(),
  /** Linear state *name* a ticket moves to when a build starts. Unset: the team's first "started" state. */
  linearBuildState: z.string().min(1).max(100).optional(),
  /** Linear state *name* a ticket moves to when its PR opens. Unset: the state is left alone. */
  linearPrState: z.string().min(1).max(100).optional(),
  /** Linear state *name* a ticket moves to when its PR merges. Unset: the state is left alone. */
  linearMergedState: z.string().min(1).max(100).optional(),
  /** Older name for linearPrState, still read when that is unset. */
  linearReviewState: z.string().min(1).max(100).optional(),
  /** Ticket branches are `<prefix><ticket>-<slug>`; default `trisof/`. */
  branchPrefix: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._\/-]*$/).max(40).optional(),
  /** Git author and committer for the robot's commits on this project. */
  authorName: z.string().min(1).max(100).optional(),
  authorEmail: z.string().email().optional(),
  /** The client's production Supabase project ref: a verify or browser-proof run refuses to start if any project env points at it. */
  prodSupabaseRef: z.string().regex(/^[a-z0-9]{6,40}$/).optional(),
  /** The production Supabase url, for error review; default https://<prodSupabaseRef>.supabase.co. */
  supabaseUrl: z.string().url().optional(),
  /** The project's Slack channel id, e.g. C0123ABCD: the morning summary and spike alerts go there (features/16). */
  slackChannel: z.string().regex(/^[CG][A-Z0-9]{6,20}$/).optional(),
  /** New Linear tickets are triaged by Claude, then wait (features/07). Default on. */
  autoTriage: z.boolean().optional(),
  /** The parts of the app triage files tickets under, e.g. "auth", "billing". */
  areas: z.array(z.string().trim().min(1).max(60)).max(50).optional(),
  /** Ticket runs at once across every server (features/09). Default 4. */
  parallelCap: z.number().int().min(1).max(32).optional(),
  /** Slack conversations with Claude at once, per server (chat_v1). Default 2. A conversation whose ticket is building counts as a ticket run instead. */
  chatCap: z.number().int().min(1).max(16).optional(),
  /**
   * Start a ticket by itself when triage finds it matches `rule` (free text
   * Claude reads). Default off. Held and out-of-scope tickets never start by
   * themselves, and only tickets triaged while it is on are considered.
   */
  autoStart: z.object({ on: z.boolean(), rule: z.string().trim().max(1000) }).optional(),
  /** Triage waits until the project has an approved scope (no Claude run before); leads are asked once. Default off. */
  requireScope: z.boolean().optional(),
  /** The house ticket flow switches (docs/ticketing.md); both off by default. */
  ticketing: TicketingSwitches.optional(),
  /** The dashboard's chat (`workflow.chat`, features/26): `stream` runs Claude in stream-json mode. Off by default. */
  chat: ChatSwitches.optional(),
  /** Autopilot (features/07): every triaged ticket builds by itself, very hard ones go to a developer, easy ones merge on green. */
  autopilot: z.boolean().optional(),
  /** Autopilot merges a PR by itself up to this difficulty once every check is green. Default easy. */
  autoMergeUpTo: z.enum(['easy', 'medium']).optional(),
  /** Linear user id of the project lead: gets very hard tickets, tickets triage could not judge, and hard ones nobody on the team has room for. */
  leadLinearUserId: z.string().min(1).max(100).optional(),
  /** Linear user id of the agent's account (e.g. "TriSof Agent"): easy and medium tickets are assigned to it. Unset: they are left unassigned. */
  agentLinearUserId: z.string().min(1).max(100).optional(),
  /** A team member with this many open tickets in the team gets no more hard ones. Default 5. */
  maxOpenTickets: z.number().int().min(1).max(100).optional(),
  /** Work tickets with the eight-step flow (features/10). Default on. */
  ticketFlow: z.boolean().optional(),
  /** Minutes a person must be quiet in a session before Claude carries on (features/12). */
  resumeAfterMin: z.number().int().min(1).max(240).optional(),
  /** Environment for every test run: the box's suite, the baseline and the repo's own pre-push hook. E.g. VITEST_MAX_WORKERS=3. */
  /** The container gets a Docker helper for the local Supabase stack (project file local.supabase). */
  localSupabase: z.boolean().optional(),
  testEnv: z.record(z.string().regex(/^[A-Z_][A-Z0-9_]*$/).max(64), z.string().max(200)).optional(),
})
export type ProjectSettings = z.infer<typeof ProjectSettings>

export const RepoInfo = z.object({
  id: z.number().int(),
  owner: z.string(),
  name: z.string(),
  defaultBranch: z.string(),
  private: z.boolean(),
  state: z.enum(['ok', 'missing']),
  missingReason: z.string().nullable(),
  missingSince: z.number().nullable(),
})
export type RepoInfo = z.infer<typeof RepoInfo>

export const StageCounts = z.record(Stage, z.number().int())
export type StageCounts = z.infer<typeof StageCounts>

export const GithubAuth = z.enum(['app', 'token'])
export type GithubAuth = z.infer<typeof GithubAuth>

/** The secret name the personal access token is kept under. Never handed to a container. */
export const GITHUB_TOKEN_SECRET = 'TRISOF_GITHUB_TOKEN'

/** Token mode: the repo is looked up with the token itself when the project is created. */
export const GithubTokenRepo = z.object({
  owner: z.string().regex(/^[A-Za-z0-9-]+$/).max(100),
  name: z.string().regex(/^[A-Za-z0-9._-]+$/).max(100),
  token: z.string().min(20).max(400),
  user: z.string().max(100).optional(),
})
export type GithubTokenRepo = z.infer<typeof GithubTokenRepo>

export const Project = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  repo: RepoInfo,
  targetBranch: z.string().nullable(),
  linearTeamId: z.string().nullable(),
  linearProjectId: z.string().nullable(),
  /** `app`: the GitHub App's installation tokens. `token`: a personal access token (no webhooks; PRs are polled). */
  githubAuth: GithubAuth,
  settings: ProjectSettings,
  archived: z.boolean(),
  role: Role,
  pendingPrs: z.number().int(),
  /** Delivery stage (docs/features/02). */
  stage: z.enum(PROJECT_STAGES),
  ...Versioned,
})
export type Project = z.infer<typeof Project>
export const ProjectList = z.object({ projects: z.array(Project) })
export type ProjectList = z.infer<typeof ProjectList>

/** Services connected with a pasted key (Connections page). */
export const CONNECTION_PROVIDERS = ['github', 'slack', 'cloudflare', 'supabase', 'trigger', 'vercel', 'expo', 'firebase', 'google-play', 'app-store'] as const
export const ConnectionProvider = z.enum(CONNECTION_PROVIDERS)
export type ConnectionProvider = z.infer<typeof ConnectionProvider>
export const Connection = z.object({
  id: z.string(),
  provider: ConnectionProvider,
  label: z.string(),
  /** Who the key belongs to, as the service named it (user, team, project). */
  account: z.string().nullable(),
  last4: z.string().nullable(),
  status: z.enum(['ok', 'failed']),
  error: z.string().nullable(),
  checkedAt: z.number(),
  createdAt: z.number(),
  /** Projects get this account unless they pick another. */
  isDefault: z.boolean(),
  /** Project keys that use this account (explicitly or as the default). */
  usedBy: z.array(z.string()),
})
export type Connection = z.infer<typeof Connection>

/**
 * What a project runs on. An account left out of `accounts` (or null) means
 * the provider's default connection. Only these accounts' keys reach the
 * project's sessions.
 */
export const ProjectServicesConfig = z.object({
  frontend: z.enum(['cloudflare', 'vercel', 'none']),
  backend: z.enum(['cloudflare', 'vercel', 'none']),
  jobs: z.enum(['cloudflare', 'trigger', 'none']),
  database: z.enum(['supabase', 'none']),
  /** Expo, Google Play and App Store Connect. */
  mobile: z.boolean(),
  /** Firebase Cloud Messaging. */
  push: z.boolean(),
  accounts: z.record(ConnectionProvider, z.string().nullable()).default({}),
})
export type ProjectServicesConfig = z.infer<typeof ProjectServicesConfig>
export const DEFAULT_SERVICES: ProjectServicesConfig = { frontend: 'cloudflare', backend: 'cloudflare', jobs: 'cloudflare', database: 'supabase', mobile: false, push: false, accounts: {} }
export const ProjectServices = z.object({
  /** Null until someone confirms it. */
  config: ProjectServicesConfig.nullable(),
  /** Read from the repo's files; what to confirm when `config` is null. */
  detected: ProjectServicesConfig.nullable(),
  /** Why each detected choice was made, e.g. "vercel.json". */
  evidence: z.array(z.string()),
  /** Env var names the sessions get from these choices (never values). */
  envNames: z.array(z.string()),
  updatedAt: z.number().nullable(),
})
export type ProjectServices = z.infer<typeof ProjectServices>

/**
 * MCP servers people's Claude Code sessions get for a project (Settings →
 * Claude Code integrations). Production only: Supabase's hosted MCP on the
 * prod project, Stripe's on the live account. Keys are written as secrets
 * (MCP_SUPABASE_ACCESS_TOKEN, MCP_STRIPE_SECRET_KEY) and never sent back.
 */
export const MCP_SERVERS = ['supabase', 'stripe'] as const
export const McpServerId = z.enum(MCP_SERVERS)
export type McpServerId = z.infer<typeof McpServerId>
export const McpConfig = z.object({
  supabase: z.object({
    enabled: z.boolean(),
    /** Read-only stops Claude writing to the production database through the MCP. */
    readOnly: z.boolean().default(true),
    /** Empty: the project's production Supabase ref. */
    ref: z.string().regex(/^[a-z0-9]{20}$/).nullable().default(null),
  }).default({ enabled: false, readOnly: true, ref: null }),
  stripe: z.object({ enabled: z.boolean() }).default({ enabled: false }),
})
export type McpConfig = z.infer<typeof McpConfig>
export const McpConfigPut = McpConfig.extend({
  /** New credentials; absent keeps the stored one, '' removes it. */
  supabaseToken: z.string().max(400).optional(),
  stripeKey: z.string().max(400).optional(),
})
export type McpConfigPut = z.infer<typeof McpConfigPut>
export const McpServerStatus = z.object({
  id: McpServerId,
  name: z.string(),
  enabled: z.boolean(),
  /** off: not enabled; missing: enabled but a key or ref is not set; error: the key was refused. */
  state: z.enum(['connected', 'error', 'missing', 'off']),
  detail: z.string(),
  /** Last four characters of the key in use, if any. */
  keyLast4: z.string().nullable(),
  /** Where the key comes from: this page, or a Connections account. */
  keySource: z.enum(['secret', 'connection']).nullable(),
  checkedAt: z.number(),
})
export type McpServerStatus = z.infer<typeof McpServerStatus>
export const McpStatus = z.object({ config: McpConfig, servers: z.array(McpServerStatus), updatedAt: z.number().nullable() })
export type McpStatus = z.infer<typeof McpStatus>

export const ProjectCreate = z.object({
  key: z.string().regex(/^[a-z][a-z0-9-]*$/).max(32),
  name: z.string().min(1).max(100),
  /** GitHub numeric repo id: a project cannot exist without a repo (decision 13). App mode. */
  repoId: z.number().int().positive().optional(),
  /** Token mode instead of `repoId`: a repo the App is not installed on. */
  githubToken: GithubTokenRepo.optional(),
  targetBranch: z.string().optional(),
  linearTeamId: z.string().optional(),
  linearProjectId: z.string().optional(),
  settings: ProjectSettings.default({}),
  /** Confirmed services; left out, the project starts unconfirmed. */
  services: ProjectServicesConfig.optional(),
}).refine((b) => (b.repoId === undefined) !== (b.githubToken === undefined), { message: 'Give exactly one of repoId (GitHub App) or githubToken (personal access token)' })
export type ProjectCreate = z.infer<typeof ProjectCreate>

// ── New-project setup (spec 06) ────────────────────────────────────────────
/** Ticked in Create project; unticked ones are stripped from main-template by its generator. */
export const TEMPLATE_ADDONS = ['stripe', 'orgs', 'analytics', 'i18n'] as const
export const TemplateAddon = z.enum(TEMPLATE_ADDONS)
export type TemplateAddon = z.infer<typeof TemplateAddon>
/** Checklist order. trigger and expo are skipped when not chosen. */
/** secrets, apply and hosts run only for a project recreated from its file. */
export const SETUP_STEPS = ['repo', 'branches', 'project', 'secrets', 'apply', 'supabase', 'cloudflare', 'trigger', 'expo', 'links', 'hosts', 'container', 'session'] as const
export const SetupStepName = z.enum(SETUP_STEPS)
export type SetupStepName = z.infer<typeof SetupStepName>

export const ProjectSetupCreate = z.object({
  key: z.string().regex(/^[a-z][a-z0-9-]*$/).min(2).max(32),
  name: z.string().min(1).max(100),
  services: ProjectServicesConfig,
  addons: z.array(TemplateAddon).default([]),
  /** Picked from Linear; setup never creates one. */
  linearTeamId: z.string().optional(),
  linearProjectId: z.string().optional(),
  /** Picked from Slack; setup never creates one. */
  slackChannel: z.string().regex(/^[CG][A-Z0-9]{6,20}$/).optional(),
  /** A .trisof/project.yml: the project is recreated from it instead of main-template (features/23). */
  file: z.string().max(200_000).optional(),
})
export type ProjectSetupCreate = z.infer<typeof ProjectSetupCreate>
/** Recreate a project from its file, with the secret values as `NAME=value` lines. Values are sealed at once and never returned. */
export const ProjectFromFile = z.object({ file: z.string().min(1).max(200_000), secrets: z.string().max(200_000).optional() })
export type ProjectFromFile = z.infer<typeof ProjectFromFile>

export const SetupStep = z.object({
  step: SetupStepName,
  state: z.enum(['pending', 'running', 'done', 'failed', 'skipped']),
  error: z.string().nullable(),
  /** What the step made or found (names, refs, urls). Never a secret. */
  output: z.record(z.unknown()).nullable(),
  tries: z.number().int(),
  updatedAt: z.number(),
})
export type SetupStep = z.infer<typeof SetupStep>
export const ProjectSetup = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  state: z.enum(['running', 'failed', 'done']),
  projectKey: z.string().nullable(),
  steps: z.array(SetupStep),
  createdAt: z.number(),
})
export type ProjectSetup = z.infer<typeof ProjectSetup>
export const ProjectSetupList = z.object({ setups: z.array(ProjectSetup) })
export type ProjectSetupList = z.infer<typeof ProjectSetupList>
export const SetupOptions = z.object({
  linearProjects: z.array(z.object({ id: z.string(), name: z.string(), teamIds: z.array(z.string()) })),
  slackChannels: z.array(z.object({ id: z.string(), name: z.string() })),
  /** Why a list is empty when it is (not connected, call failed). */
  notes: z.array(z.string()),
  /** main-template is registered as an internal project. */
  templateRegistered: z.boolean(),
})
export type SetupOptions = z.infer<typeof SetupOptions>

export const ProjectPatch = z.object({
  name: z.string().min(1).max(100).optional(),
  targetBranch: z.string().nullable().optional(),
  linearTeamId: z.string().nullable().optional(),
  linearProjectId: z.string().nullable().optional(),
  settings: ProjectSettings.optional(),
  /** Settings to remove (back to their default). JSON cannot carry `undefined`, so clearing needs its own list. */
  clearSettings: z.array(ProjectSettings.keyof()).optional(),
})
export type ProjectPatch = z.infer<typeof ProjectPatch>

export const RepoReconnect = z.object({ repoId: z.number().int().positive() })
export type RepoReconnect = z.infer<typeof RepoReconnect>

/** Secrets are write-only: a read gives names and the last four characters. */
/**
 * Which environment a value is for (features/23): '' every environment, `local`
 * Claude's container (dev), else an environment of the project file (uat, prod).
 * An environment uses its own value when there is one, else the '' one.
 */
export const SecretEnv = z.string().regex(/^(|[a-z][a-z0-9-]{0,30})$/, 'an environment name, or empty for all').default('')
export const SecretMeta = z.object({ name: z.string(), env: z.string().default(''), last4: z.string().nullable(), updatedAt: z.number(), updatedBy: z.string().nullable() })
export type SecretMeta = z.infer<typeof SecretMeta>
/** A host whose env vars only a person can set (features/23). */
export const SecretHost = z.enum(['trigger'])
export const SecretMark = z.object({ name: z.string(), env: z.string(), host: SecretHost, markedAt: z.number(), markedBy: z.string().nullable() })
export type SecretMark = z.infer<typeof SecretMark>
export const SecretList = z.object({
  secrets: z.array(SecretMeta),
  /** Who said a value was added to a manual host, and when. */
  marks: z.array(SecretMark).default([]),
  /** What the applied .trisof/project.yml names, against what is stored; null without a file. */
  file: z.object({
    wanted: z.array(z.object({
      name: z.string(), description: z.string().nullable(), required: z.boolean(), stored: z.boolean(), envs: z.array(z.string()).nullable(), apps: z.array(z.string()).nullable(),
      /** frontend: shipped to the browser/app; backend: server only; deploy: CI only. */
      kind: z.enum(['frontend', 'backend', 'deploy']).default('backend'),
      /** In Claude's container (the local column) and so in the apps' .env files there. */
      local: z.boolean().default(true),
      /** Where its value goes per environment, e.g. { uat: ['Vercel rhythm (uat)', 'GitHub env uat'] }. */
      goes: z.record(z.string(), z.array(z.string())).default({}),
      /** Hosts a person puts the value in by hand, per environment (Trigger.dev). */
      manual: z.record(z.string(), z.array(SecretHost)).default({}),
    })),
    /** The grid's columns: `local` (the container) then the file's environments. */
    columns: z.array(z.string()).default(['local']),
    /** The .env files written in the container, and which names each holds. */
    envFiles: z.array(z.object({ path: z.string(), apps: z.array(z.string()), names: z.array(z.string()) })).default([]),
    /** Stored but not in the file: nothing delivers them. */
    extra: z.array(z.string()),
  }).nullable().default(null),
})
/** Copy one stored value to another name and environment (UAT_SUPABASE_URL → SUPABASE_URL in uat); the original stays. */
export const SecretCopy = z.object({ env: SecretEnv, toName: z.string().regex(/^[A-Z_][A-Z0-9_]*$/), toEnv: SecretEnv })
/** Many values at once, as `NAME=value` lines (a .env file). */
export const SecretBulk = z.object({ text: z.string().min(1).max(200_000), env: SecretEnv })
export type SecretBulk = z.infer<typeof SecretBulk>
export const SecretBulkResult = z.object({ set: z.array(z.string()), skipped: z.array(z.object({ line: z.number(), reason: z.string() })) })
export type SecretBulkResult = z.infer<typeof SecretBulkResult>
export type SecretList = z.infer<typeof SecretList>
export const SecretPut = z.object({ value: z.string().min(1).max(32_768), env: SecretEnv })
export type SecretPut = z.infer<typeof SecretPut>
export const SecretName = z.string().regex(/^[A-Z_][A-Z0-9_]*$/)
export type SecretName = z.infer<typeof SecretName>

// ── Workspaces, jobs, sessions ─────────────────────────────────────────────

export const TicketKind = z.enum(['bug', 'feature', 'change_request', 'feedback', 'error', 'chore'])
/** Check levels: which extra checks a build of the ticket needs, beyond build, lint, typecheck and unit tests. */
export const CHECK_LEVELS = [
  'logic', 'design', 'flow', 'text', 'data', 'access', 'api', 'integration', 'performance',
  'dependency', 'responsive', 'accessibility', 'security', 'money', 'dates', 'notifications', 'reports', 'bug_fix',
] as const
export const CheckLevel = z.enum(CHECK_LEVELS)
export type CheckLevel = z.infer<typeof CheckLevel>
/** Where a Linear priority sorts: 1 urgent first … 4 low, then 0 or none last. */
export const priorityRank = (p: number | null | undefined): number => (typeof p === 'number' && p >= 1 && p <= 4 ? p : 5)

/** What triage found when the ticket came in, shown on its card. */
export const TriageBrief = z.object({
  kind: TicketKind,
  /** Linear priority: 1 urgent, 2 high, 3 medium, 4 low. */
  priority: z.number().int().min(1).max(4),
  size: z.enum(['S', 'M', 'L']),
  area: z.string(),
  scope: z.enum(['in', 'out', 'unknown']),
  estimateHours: z.number().nullable(),
  held: z.boolean(),
  levels: z.array(CheckLevel),
  /** A person set the levels by hand; a build never replaces them. */
  levelsByPerson: z.boolean().optional(),
  /** How hard triage judged it: very_hard goes to a developer; an easy one's PR merges itself on autopilot. */
  difficulty: z.enum(['easy', 'medium', 'hard', 'very_hard']).nullable().optional(),
})
export type TriageBrief = z.infer<typeof TriageBrief>

export const WorkspaceSummary = z.object({
  id: z.string(),
  projectKey: z.string(),
  ticket: z.string(),
  title: z.string(),
  url: z.string(),
  branch: z.string(),
  stage: Stage,
  stageReason: z.string().nullable(),
  prNumber: z.number().int().nullable(),
  prState: z.enum(['open', 'merged', 'integrated', 'closed']).nullable(),
  /** tmux clients attached to the session right now. Presence only: nothing pauses for a person. */
  attached: z.number().int(),
  liveJob: z.object({ id: z.string(), mode: JobMode, status: JobStatus, phase: z.string().nullable() }).nullable(),
  updatedAt: z.number(),
  /** The Claude account chosen for this workspace's runs; null = the agent default. */
  claudeAccountId: z.string().nullable(),
  /** The ticket's Linear state name as mirrored; null when unknown. */
  linearState: z.string().nullable(),
  /** Null until the ticket is triaged. */
  triage: TriageBrief.nullable().optional(),
  /** Linear's own priority (0 none, 1 urgent … 4 low), for ordering when triage has not set one. */
  linearPriority: z.number().int().nullable().optional(),
})
export type WorkspaceSummary = z.infer<typeof WorkspaceSummary>

/** A ticket in the project's incoming Linear state that has no workspace yet. */
export const ReadyTicket = z.object({ ticket: z.string(), title: z.string(), url: z.string(), updatedAt: z.number(), triage: TriageBrief.nullable().optional(), linearPriority: z.number().int().nullable().optional() })
export type ReadyTicket = z.infer<typeof ReadyTicket>
export const Board = z.object({
  workspaces: z.array(WorkspaceSummary),
  counts: StageCounts,
  /** The project's incoming Linear state (settings.linearTriggerState); null = unset, Ready to start shows every triaged ticket. */
  incomingState: z.string().nullable(),
  /** Tickets sitting in the incoming state with no workspace: Ready to start shows them too. */
  readyTickets: z.array(ReadyTicket),
})
export type Board = z.infer<typeof Board>

export const Job = z.object({
  id: z.string(),
  workspaceId: z.string(),
  mode: JobMode,
  source: JobSource,
  status: JobStatus,
  phase: z.string().nullable(),
  checkpoint: z.string().nullable(),
  attempts: z.number().int(),
  maxAttempts: z.number().int(),
  notBefore: z.number(),
  leaseBox: z.string().nullable(),
  outcome: z.unknown().nullable(),
  error: z.string().nullable(),
  createdAt: z.number(),
  startedAt: z.number().nullable(),
  finishedAt: z.number().nullable(),
})
export type Job = z.infer<typeof Job>

export const Workspace = WorkspaceSummary.extend({
  containerId: z.string().nullable(),
  tmuxSession: z.string().nullable(),
  serverHost: z.string().nullable(),
  jobs: z.array(Job),
})
export type Workspace = z.infer<typeof Workspace>

export const TimelineEvent = z.object({
  id: z.number().int(),
  jobId: z.string(),
  at: z.number(),
  kind: z.string(),
  body: z.unknown(),
})
export type TimelineEvent = z.infer<typeof TimelineEvent>
/** The ticket panel in plain words (services/plain.ts). */
export const TicketStory = z.object({
  status: z.string(),
  /** What a person needs to do; null when nothing. */
  needed: z.string().nullable(),
  /** The raw reason, for the "Technical details" fold. */
  details: z.string().nullable(),
  attempts: z.array(z.object({ n: z.number().int(), at: z.number(), steps: z.array(z.object({ at: z.number(), label: z.string(), bad: z.boolean() })) })),
  /** The Slack thread the ticket came from, when it did. */
  slackUrl: z.string().nullable().optional(),
  /**
   * Why it waits: Claude asked something (`question`), it stopped and needs a
   * look (`stuck`), or a pull request is ready (`review`). Null when nothing waits.
   */
  waitingOn: z.enum(['question', 'stuck', 'review']).nullable().optional(),
  /** Claude's question (ask_human, or triage's), when `waitingOn` is `question`. */
  question: z.object({ text: z.string(), options: z.array(z.string()), context: z.string().nullable() }).nullable().optional(),
})
export type TicketStory = z.infer<typeof TicketStory>
/** A picture Claude made for the person following the ticket (`.trisof/outbox/`). */
export const Visual = z.object({
  id: z.string(),
  name: z.string(),
  /** Read from the file name: `option-a-compact-list.html` is "Option a compact list". */
  title: z.string(),
  format: z.enum(['mermaid', 'svg', 'html', 'image', 'markdown']),
  jobId: z.string(),
  /** What the run was doing: build, reply, fix. */
  mode: z.string(),
  at: z.number(),
  bytes: z.number().int(),
  /** Made with the question that waits now. */
  forQuestion: z.boolean(),
  url: z.string(),
})
export type Visual = z.infer<typeof Visual>
export const VisualList = z.object({ visuals: z.array(Visual) })
export type VisualList = z.infer<typeof VisualList>
export const Timeline = z.object({ events: z.array(TimelineEvent) })
export type Timeline = z.infer<typeof Timeline>

export const CheckRun = z.object({ name: z.string(), status: z.string(), conclusion: z.string().nullable(), url: z.string().nullable() })
export type CheckRun = z.infer<typeof CheckRun>
export const Checks = z.object({ checks: z.array(CheckRun) })
export type Checks = z.infer<typeof Checks>
export const Diff = z.object({ base: z.string(), head: z.string(), patch: z.string(), truncated: z.boolean() })
export type Diff = z.infer<typeof Diff>

export const AgentSession = z.object({
  id: z.string(),
  jobId: z.string().nullable(),
  workspaceId: z.string().nullable(),
  ticket: z.string().nullable(),
  projectKey: z.string().nullable(),
  containerId: z.string(),
  tmuxSession: z.string(),
  task: z.string(),
  model: z.string(),
  writes: z.boolean(),
  startedBy: z.string().nullable(),
  startedAt: z.number(),
  heartbeatAt: z.number(),
  finishedAt: z.number().nullable(),
  outcome: z.string().nullable(),
  /** The Claude account the session runs on; null = the project-secret fallback (no accounts). */
  claudeAccountId: z.string().nullable(),
  /** What people call it ("Session 3"); null for robot sessions. */
  label: z.string().nullable(),
  /** The person whose session it is (their git identity); null for the robot's. Optional: older payloads lack it. */
  ownerId: z.string().nullable().optional(),
  ownerName: z.string().nullable().optional(),
})
export type AgentSession = z.infer<typeof AgentSession>
export const SessionsList = z.object({ sessions: z.array(AgentSession) })
export type SessionsList = z.infer<typeof SessionsList>

export const JobCreate = z.object({
  mode: z.enum(['build', 'fix', 'verify', 'screen', 'classify', 'reply']),
  accountId: z.string().max(64).optional(),
  /** A reply's answer to the agent's question; required for mode 'reply'. */
  text: z.string().trim().min(1).max(8000).optional(),
  /** The reply is an option chosen with the Choose button under its picture (pictures.ts learns from it). */
  picked: z.boolean().optional(),
})
export type JobCreate = z.infer<typeof JobCreate>
export const JobCreated = z.object({ job: Job })
export type JobCreated = z.infer<typeof JobCreated>

export const SessionCreate = z.object({ name: z.string().regex(/^[a-zA-Z0-9_-]+$/).max(64).optional(), accountId: z.string().max(64).optional() })
export type SessionCreate = z.infer<typeof SessionCreate>
export const SessionCreated = z.object({ tmuxSession: z.string() })
export type SessionCreated = z.infer<typeof SessionCreated>
/** A project's delivery stages, with each one's checklist and what is happening in it. */
export const StageCheckDto = z.object({ key: z.string(), label: z.string(), done: z.boolean(), by: z.string().nullable(), at: z.number().nullable() })
export const StageStep = z.object({
  stage: z.enum(PROJECT_STAGES),
  label: z.string(),
  summary: z.string(),
  status: z.enum(['done', 'skipped', 'current', 'upcoming']),
  by: z.string().nullable(),
  at: z.number().nullable(),
  reason: z.string().nullable(),
  checks: z.array(StageCheckDto),
})
export const ProjectStages = z.object({
  current: z.enum(PROJECT_STAGES),
  steps: z.array(StageStep),
  /** What is happening now: Linear tickets by state type, sessions and PRs. */
  now: z.object({ ticketsOpen: z.number().int(), ticketsStarted: z.number().int(), ticketsDone: z.number().int(), sessions: z.number().int(), prsOpen: z.number().int(), workspacesActive: z.number().int() }),
})
export type ProjectStages = z.infer<typeof ProjectStages>
export const StageCheckSet = z.object({ stage: z.enum(PROJECT_STAGES), item: z.string().min(1).max(40), done: z.boolean() })
export type StageCheckSet = z.infer<typeof StageCheckSet>
/** Move on from `from` (must be the current stage). Open checks need `override`; a skip needs `reason`. */
export const StageAdvance = z.object({ from: z.enum(PROJECT_STAGES), skip: z.boolean().optional(), reason: z.string().trim().max(500).optional(), override: z.boolean().optional() })
export type StageAdvance = z.infer<typeof StageAdvance>
export const SessionRename = z.object({ label: z.string().trim().min(1).max(60) })
export type SessionRename = z.infer<typeof SessionRename>
/** Open Linear tickets of the project's team, counted per workflow state, in Linear's order. */
export const TicketCounts = z.object({ states: z.array(z.object({ name: z.string(), type: z.string(), count: z.number().int() })) })
export type TicketCounts = z.infer<typeof TicketCounts>
/** One Linear ticket on the Tickets board, read from the mirror. `status` is the column (docs/ticketing.md); `stateName` is Linear's own. */
export const CycleLoad = z.object({ planned: z.number(), done: z.number(), tickets: z.number().int(), unestimated: z.number().int() })
export const CycleSummary = CycleLoad.extend({
  id: z.string(), number: z.number().int(), name: z.string().nullable(), startsAt: z.number(), endsAt: z.number(),
})
export type CycleSummary = z.infer<typeof CycleSummary>
export const BoardTicket = z.object({
  id: z.string(), ticket: z.string(), title: z.string(), url: z.string(),
  status: z.string(), stateName: z.string().nullable(),
  owner: z.string().nullable(),
  /** The owner's Linear profile picture. */
  ownerAvatar: z.string().nullable().optional(), priority: z.number().int().nullable(), estimate: z.number().nullable(),
  scope: z.string().nullable(), source: z.string().nullable(), labels: z.array(z.string()),
  updatedAt: z.number(),
  /** When it was made in Linear; null until the mirror has read it. */
  createdAt: z.number().nullable().optional(),
  /** The Linear cycle it is planned in, if any. */
  cycleId: z.string().nullable(),
  /** The Linear assignee's id, for the load by developer. */
  ownerId: z.string().nullable(),
  /** Description and comments, for the board's search. */
  text: z.string(),
  /** The robot's workspace on it, if any: its stage, and whether it is waiting on a person. */
  workspace: z.object({ id: z.string(), stage: z.string(), needsYou: z.boolean() }).nullable(),
  /**
   * What a person has to do on it (docs/ticketing.md): a ticket with any shows
   * in Needs attention, and clicking it shows these. `triage_review`: accept or
   * change Claude's recommendation; `agent`: the robot is waiting on a person.
   */
  tasks: z.array(z.object({
    id: z.string(), kind: z.enum(['triage_review', 'agent']), title: z.string(),
    assigneeId: z.string().nullable(), assignee: z.string().nullable(), createdAt: z.number(),
  })).optional(),
  /** Claude's triage suggestion while the ticket is in Triage (docs/ticketing.md); the lead accepts or edits it. */
  suggestion: z.object({
    status: z.string().nullable(), why: z.string().nullable(), points: z.number().int().nullable(), priority: z.number().int().nullable(),
    scope: z.string().nullable(), ownerId: z.string().nullable(), owner: z.string().nullable(), accepted: z.boolean(),
  }).nullable(),
})
export type BoardTicket = z.infer<typeof BoardTicket>
export const TicketBoard = z.object({
  tickets: z.array(BoardTicket), missing: z.array(z.string()), linked: z.boolean(),
  /** Whether Accept and the agent's status moves write to Linear (`workflow.ticketing.linearWrites`). On unless the project file turns it off; off: nothing changes a ticket. */
  linearWrites: z.boolean(),
  /** Who a ticket can be given to: the team's Linear users. */
  people: z.array(z.object({ id: z.string(), name: z.string(), avatar: z.string().nullable().optional() })),
  /**
   * Weekly cycles (docs/ticketing.md). `weekly`: the Linear team runs
   * one-week cycles. Capacity is the team's people (the agent not counted)
   * times their points a week; planned and done are estimate points.
   */
  cycles: z.object({
    enabled: z.boolean(), weekly: z.boolean(), pointsPerPerson: z.number(), capacity: z.number(),
    /** This week's work: the running cycle's tickets, plus open Todo and In Progress ones in no cycle. */
    week: CycleLoad,
    /** Each developer's load this week against their points. */
    people: z.array(CycleLoad.extend({ id: z.string(), name: z.string(), linked: z.boolean(), capacity: z.number() })),
    unassigned: z.number(), others: z.number(),
    current: CycleSummary.nullable(), next: CycleSummary.nullable(),
    list: z.array(z.object({ id: z.string(), number: z.number().int(), name: z.string().nullable(), startsAt: z.number(), endsAt: z.number() })),
  }).optional(),
})
/** One ticket in full for the board's side panel, read live from Linear (the mirror when Linear cannot be read). */
export const TicketDetail = z.object({
  description: z.string().nullable(),
  createdAt: z.number().nullable(), creator: z.string().nullable(), cycle: z.string().nullable(),
  comments: z.array(z.object({ id: z.string(), by: z.string(), avatar: z.string().nullable().optional(), body: z.string(), at: z.number() })),
  attachments: z.array(z.object({ title: z.string(), url: z.string() })),
  /** Set when Linear could not be read and the mirror was used. */
  note: z.string().nullable(),
})
export type TicketDetail = z.infer<typeof TicketDetail>
/** The lead's decision on a ticket in Triage: Claude's suggestion as is, or edited. */
export const TicketAccept = z.object({
  status: z.enum(['backlog', 'todo', 'blocked', 'client_response', 'out_of_scope', 'duplicate']),
  ownerId: z.string().min(1).max(100).nullable().optional(),
  priority: z.number().int().min(1).max(4).nullable().optional(),
  points: z.union([z.literal(1), z.literal(2), z.literal(4), z.literal(8), z.literal(16)]).nullable().optional(),
  scope: z.enum(['In scope', 'Out of scope']).optional(),
})
export type TicketAccept = z.infer<typeof TicketAccept>
/** A ticket dragged to another column: any Linear status but Needs attention, which is ours only. */
export const TicketMove = z.object({
  status: z.enum(['triage', 'backlog', 'todo', 'in_progress', 'needs_merge', 'ready_to_test', 'done', 'released', 'blocked', 'client_response', 'out_of_scope', 'duplicate']),
})
export type TicketMove = z.infer<typeof TicketMove>
/** A ticket's fields changed on the board: only the ones given; null clears. */
export const TicketFields = z.object({
  points: z.union([z.literal(1), z.literal(2), z.literal(4), z.literal(8), z.literal(16)]).nullable().optional(),
  ownerId: z.string().min(1).max(100).nullable().optional(),
  priority: z.number().int().min(0).max(4).optional(),
  cycleId: z.string().min(1).max(100).nullable().optional(),
}).refine((v) => Object.keys(v).length > 0, 'Nothing to change')
export type TicketFields = z.infer<typeof TicketFields>
export type TicketBoard = z.infer<typeof TicketBoard>
/** What a project's Linear settings pick from, read live from Linear for one team. Colors are Linear's hex; a null team means Linear could not be read. */
export const LinearOptions = z.object({
  team: z.object({ id: z.string(), key: z.string(), name: z.string(), color: z.string().nullable() }).nullable(),
  projects: z.array(z.object({ id: z.string(), name: z.string(), color: z.string().nullable() })),
  states: z.array(z.object({ name: z.string(), type: z.string(), color: z.string().nullable() })),
  /** The Linear workspace's people (synced), for picking the lead and the agent. Absent from an older worker. */
  users: z.array(z.object({ id: z.string(), name: z.string(), email: z.string().nullable() })).optional(),
  note: z.string().nullable(),
})
export type LinearOptions = z.infer<typeof LinearOptions>
/** The repo's branches, default and target first. */
export const BranchList = z.object({ branches: z.array(z.string()), note: z.string().nullable() })
export type BranchList = z.infer<typeof BranchList>

/** The repo's git picture for a session's git bar: open PRs into the base, and how far base is ahead of prod. */
export const RepoGitPr = z.object({
  number: z.number().int(), title: z.string(), url: z.string(), head: z.string(), author: z.string().nullable(),
  /** The author's name here, when a person's GitHub login (Account → links) matches. */
  person: z.string().nullable(),
  draft: z.boolean(), checks: z.enum(['green', 'red', 'pending', 'none']), mergeable: z.boolean().nullable(),
})
export type RepoGitPr = z.infer<typeof RepoGitPr>
export const RepoGit = z.object({
  base: z.string(), prod: z.string(),
  prs: z.array(RepoGitPr),
  /** Commits on base not yet on prod; null when GitHub could not say. */
  aheadOfProd: z.number().int().nullable(),
  lastProdRun: z.object({ status: z.string(), conclusion: z.string().nullable(), url: z.string(), at: z.string() }).nullable(),
  note: z.string().nullable(),
  /** The project file sets a prod workflow (flow.ship.workflow): the dashboard offers one Push to prod button. */
  ship: z.boolean(),
  /** flow.ship.ci: the CI schedule the ship runs, so the dashboard can draw its jobs before the run starts. */
  shipCi: z.string().nullable().optional(),
  /** flow.push: one button per environment, each merging `base` into `prod` (that env's branch). */
  pushes: z.array(z.object({ env: z.string(), base: z.string(), prod: z.string() })).optional(),
})
export type RepoGit = z.infer<typeof RepoGit>

// ── Merge into the base branch, ship to prod (features/24) ──
export const RELEASE_KINDS = ['merge', 'ship'] as const
/** env: Push to that environment (flow.push); left out, the one Push to prod. */
export const ReleaseStart = z.object({
  kind: z.enum(RELEASE_KINDS), env: z.string().max(40).optional(),
  /** A merge of just these tickets' PRs (board ids, i.e. Linear issue ids), one after another; otherwise every open PR. */
  tickets: z.array(z.string().min(1).max(100)).min(1).max(100).optional(),
}).strict()
export type ReleaseStart = z.infer<typeof ReleaseStart>
export const ReleasePr = z.object({
  number: z.number().int(), title: z.string(), url: z.string(), author: z.string().nullable(),
  /** waiting: checks or GitHub still deciding; fixing: sent back to Claude; left: not merged, see why. */
  state: z.enum(['waiting', 'fixing', 'merged', 'left']),
  why: z.string().nullable(),
})
export type ReleasePr = z.infer<typeof ReleasePr>
export const ReleaseCheck = z.object({
  name: z.string(),
  state: z.enum(['pending', 'running', 'passed', 'failed', 'skipped']),
  seconds: z.number().int().nullable(),
  /** The end of a failed check's output. */
  tail: z.string().nullable(),
})
export type ReleaseCheck = z.infer<typeof ReleaseCheck>
export const ReleaseRun = z.object({
  id: z.number().int().nullable(), url: z.string().nullable(), status: z.string(), conclusion: z.string().nullable(),
  /** Set when the ship runs on our own CI (flow.ship.ci): the CI run's id; id and url are then unused. */
  ci: z.string().nullable().optional(),
  jobs: z.array(z.object({ name: z.string(), status: z.string(), conclusion: z.string().nullable() })),
})
export type ReleaseRun = z.infer<typeof ReleaseRun>
export const Release = z.object({
  id: z.string(), kind: z.enum(RELEASE_KINDS),
  state: z.enum(['running', 'done', 'failed', 'cancelled']),
  step: z.enum(['merge', 'checks', 'ship', 'watch', 'end']),
  base: z.string(), prod: z.string(), sha: z.string().nullable(),
  startedBy: z.object({ id: z.string(), name: z.string(), githubLogin: z.string().nullable() }),
  startedAt: z.number().int(), finishedAt: z.number().int().nullable(),
  prs: z.array(ReleasePr), checks: z.array(ReleaseCheck), run: ReleaseRun.nullable(),
  error: z.string().nullable(), version: z.number().int(),
  /** The hidden Claude session running the project's ship command (flow.ship.session); id once it is up. */
  session: z.object({ tmux: z.string(), id: z.string().nullable() }).nullable(),
  events: z.array(z.object({ at: z.number().int(), level: z.enum(['info', 'ok', 'warn', 'error']), text: z.string() })),
})
export type Release = z.infer<typeof Release>
export const ReleaseList = z.object({ current: Release.nullable(), recent: z.array(Release) })
export type ReleaseList = z.infer<typeof ReleaseList>

// ── CI (features/21): runs from .trisof/ci.yml, for projects with settings.ci.enabled ──
export const CI_RUN_STATUSES = ['queued', 'running', 'success', 'failure', 'cancelled', 'error'] as const
export const CI_JOB_STATUSES = ['waiting', 'queued', 'leased', 'success', 'failure', 'cancelled', 'skipped', 'error'] as const
export const CiRun = z.object({
  id: z.string(),
  sha: z.string(),
  branch: z.string(),
  event: z.enum(['push', 'pull_request', 'manual', 'schedule']),
  prNumber: z.number().int().nullable(),
  status: z.enum(CI_RUN_STATUSES),
  /** Why the pipeline file could not be read; set only on `error`. */
  error: z.string().nullable(),
  createdAt: z.number(),
  finishedAt: z.number().nullable(),
  /** Jobs per status, for the list; a status no job has is left out. */
  jobs: z.record(z.enum(CI_JOB_STATUSES), z.number().int()),
})
export type CiRun = z.infer<typeof CiRun>
export const CiRunList = z.object({ runs: z.array(CiRun) })
export type CiRunList = z.infer<typeof CiRunList>
export const CiJob = z.object({
  id: z.string(),
  name: z.string(),
  needs: z.array(z.string()),
  status: z.enum(CI_JOB_STATUSES),
  error: z.string().nullable(),
  exitCode: z.number().int().nullable(),
  startedAt: z.number().nullable(),
  finishedAt: z.number().nullable(),
  attempts: z.number().int(),
})
export type CiJob = z.infer<typeof CiJob>
/** One run with its jobs in the order the file lists them. */
export const CiRunDetail = z.object({
  run: CiRun, jobs: z.array(CiJob),
  /** The latest later run of the same commit and schedule (a re-run), so an old run's page points to it. */
  newer: z.string().nullable().optional(),
})
export type CiRunDetail = z.infer<typeof CiRunDetail>
/** A run by hand, on the branch's current head. */
/** schedule: run that on.schedule entry's jobs now (as its cron would, but on this branch). */
export const CiRunCreate = z.object({ branch: z.string().trim().min(1).max(255), schedule: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,39}$/).optional() })
export type CiRunCreate = z.infer<typeof CiRunCreate>
/** .trisof/project.yml as last read from the default branch (features/23). null: no file applied yet. */
export const ProjectFileStatus = z.object({
  sha: z.string(),
  status: z.enum(['applied', 'invalid']),
  problems: z.array(z.string()),
  goodSha: z.string().nullable(),
  updatedAt: z.number(),
  /** The nightly check; null until it has run. */
  drift: z.object({ drift: z.array(z.object({ what: z.string(), detail: z.string() })), unchecked: z.array(z.string()) }).nullable(),
  driftAt: z.number().nullable(),
}).nullable()
export type ProjectFileStatus = z.infer<typeof ProjectFileStatus>

/** POST /projects/:key/project-file/apply: the commit whose file is queued to apply. */
export const ProjectFileReread = z.object({ sha: z.string(), branch: z.string() })
export type ProjectFileReread = z.infer<typeof ProjectFileReread>
/** The project as a .trisof/project.yml; `problems` when what is set would not pass the checks. */
export const ProjectFileExport = z.object({ text: z.string(), problems: z.array(z.string()) })
export type ProjectFileExport = z.infer<typeof ProjectFileExport>

/** Restore from the nightly backup (features/23). Every call carries a fresh 2FA code. */
export const BackupList = z.object({ backups: z.array(z.object({ key: z.string(), size: z.number(), uploadedAt: z.number() })), ready: z.boolean(), missing: z.array(z.string()) })
export type BackupList = z.infer<typeof BackupList>
export const BackupOpen = z.object({ key: z.string().min(1).max(200), code: z.string().regex(/^\d{6}$/, 'the 6-digit code from your authenticator') })
export type BackupOpen = z.infer<typeof BackupOpen>
export const BackupContents = z.object({
  key: z.string(),
  projects: z.array(z.object({
    projectId: z.string(), key: z.string(), exists: z.boolean(),
    secrets: z.array(z.object({ name: z.string(), updatedAt: z.number(), now: z.enum(['same', 'different', 'missing']) })),
  })),
})
export type BackupContents = z.infer<typeof BackupContents>
export const BackupRestore = BackupOpen.extend({ projectId: z.string().min(1), names: z.array(z.string().regex(/^[A-Z_][A-Z0-9_]*$/)).min(1).max(200) })
export type BackupRestore = z.infer<typeof BackupRestore>
export const BackupRestored = z.object({ restored: z.array(z.string()) })
export type BackupRestored = z.infer<typeof BackupRestored>

/** What a run on a branch would do, without starting it: the dashboard's Dry run. */
export const CiDryRun = z.object({
  sha: z.string(),
  error: z.string().nullable(),
  jobs: z.array(z.object({
    name: z.string(),
    will: z.enum(['run', 'skip', 'error']),
    why: z.string().nullable(),
    needs: z.array(z.string()),
    secrets: z.array(z.string()),
    steps: z.array(z.object({ name: z.string(), run: z.string() })),
  })),
})
export type CiDryRun = z.infer<typeof CiDryRun>
export const CiRunStarted = z.object({ runId: z.string() })
export type CiRunStarted = z.infer<typeof CiRunStarted>
export const MessageSend = z.object({ text: z.string().min(1).max(20_000) })
export type MessageSend = z.infer<typeof MessageSend>

/** One stored chat message. `deliveredAt` null means it is still in the outbox, on its way to the box. */
export const WorkspaceMessage = z.object({
  id: z.string(),
  workspaceId: z.string(),
  author: z.string(),
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: z.number(),
  deliveredAt: z.number().nullable(),
})
export type WorkspaceMessage = z.infer<typeof WorkspaceMessage>
/** Newest first; pass `nextBefore` back as `?before=` for the page before. */
export const WorkspaceMessageList = z.object({ messages: z.array(WorkspaceMessage), nextBefore: z.string().nullable() })
export type WorkspaceMessageList = z.infer<typeof WorkspaceMessageList>

export const BoxTokenRequest = z.object({
  /** One of these: a ticket's workspace (its current session), or any session by id. */
  workspaceId: z.string().optional(),
  sessionId: z.string().optional(),
  /** `preview` opens the session's dev server on the box's preview host. */
  purpose: z.enum(['terminal', 'stream', 'preview', 'files', 'chat']),
  mode: z.enum(['ro', 'rw']),
  /** The session's run terminal (its dev server) instead of Claude's. */
  run: z.boolean().optional(),
  /** A bash shell in the session's worktree (tmux `<session>-sh`, ONE-11), made on first rw use. */
  shell: z.boolean().optional(),
  /** Attach to a specific tmux session in the workspace's container. */
  tmuxSession: z.string().optional(),
})
export type BoxTokenRequest = z.infer<typeof BoxTokenRequest>
export const BoxTokenResponse = z.object({ url: z.string(), token: z.string(), expiresAt: z.number() })
export type BoxTokenResponse = z.infer<typeof BoxTokenResponse>

// ── Linear mirror ───────────────────────────────────────────────────────────

export const Issue = z.object({
  id: z.string(),
  identifier: z.string(),
  title: z.string(),
  stateName: z.string().nullable(),
  stateType: z.string().nullable(),
  assignee: z.string().nullable(),
  delegated: z.boolean(),
  priority: z.number().nullable(),
  url: z.string(),
  workspaceId: z.string().nullable(),
  stage: Stage.nullable(),
  updatedAt: z.number(),
  triage: z.lazy(() => TicketTriage).nullable(),
})
export type Issue = z.infer<typeof Issue>

// ── Triage (features/07) ────────────────────────────────────────────────────

export const TicketTriage = z.object({
  kind: TicketKind,
  priority: z.number().int().min(1).max(4),
  size: z.enum(['S', 'M', 'L']),
  area: z.string(),
  scope: z.enum(['in', 'out', 'unknown']),
  scopeLines: z.string().nullable(),
  scopeReason: z.string(),
  estimateHours: z.number().nullable(),
  /** Out of scope and not yet let through by an admin or lead: it cannot be started. */
  held: z.boolean(),
  releasedBy: z.string().nullable(),
  releaseReason: z.string().nullable(),
  levels: z.array(CheckLevel),
  levelsByPerson: z.boolean().optional(),
  /** How hard triage judged it: very_hard goes to a developer; an easy one's PR merges itself on autopilot. */
  difficulty: z.enum(['easy', 'medium', 'hard', 'very_hard']).nullable().optional(),
  difficultyReason: z.string().nullable().optional(),
  at: z.number(),
})
export type TicketTriage = z.infer<typeof TicketTriage>
/** What an admin or lead decides about a held ticket. */
export const TriageDecide = z.object({ decision: z.enum(['in_scope', 'change_request', 'decline']), reason: z.string().trim().max(500).optional() })
export type TriageDecide = z.infer<typeof TriageDecide>
/** A person sets a ticket's check levels by hand, or gives them back to the build (null). */
export const LevelsSet = z.object({ levels: z.array(CheckLevel).max(18).nullable() })
export type LevelsSet = z.infer<typeof LevelsSet>
export const LevelsSetResult = z.object({ levels: z.array(CheckLevel), levelsByPerson: z.boolean(), recheckJobId: z.string().nullable() })
export type LevelsSetResult = z.infer<typeof LevelsSetResult>

/** Start many tickets at once: each its own session, branch and PR (features/09). */
export const BatchStart = z.object({ identifiers: z.array(z.string().min(1).max(40)).min(1).max(100), accountId: z.string().max(64).optional() })
export type BatchStart = z.infer<typeof BatchStart>
export const BatchStarted = z.object({
  started: z.array(z.object({ identifier: z.string(), jobId: z.string() })),
  skipped: z.array(z.object({ identifier: z.string(), reason: z.string() })),
})
export type BatchStarted = z.infer<typeof BatchStarted>

// ── Tickets from text (features/08) ────────────────────────────────────────

export const IntakeFile = z.object({ key: z.string(), name: z.string().max(200), size: z.number().int(), type: z.string().max(200) })
export type IntakeFile = z.infer<typeof IntakeFile>
export const IntakeStart = z.object({
  text: z.string().max(100_000).default(''),
  files: z.array(IntakeFile).max(10).default([]),
}).refine((b) => b.text.trim().length > 0 || b.files.length > 0, { message: 'Paste some text or add a file' })
export type IntakeStart = z.infer<typeof IntakeStart>
export const IntakeItem = z.object({
  title: z.string(),
  identifier: z.string().nullable(),
  url: z.string().nullable(),
  /** Set when the ticket was added to an open one instead of made new. */
  duplicateOf: z.string().nullable(),
  kind: z.string().nullable(),
  held: z.boolean(),
})
export const IntakeRun = z.object({
  id: z.string(),
  title: z.string(),
  status: z.enum(['creating', 'splitting', 'done', 'failed', 'undone']),
  summary: z.string().nullable(),
  issue: z.object({ identifier: z.string(), url: z.string(), stage: Stage.nullable(), reason: z.string().nullable() }).nullable(),
  files: z.array(z.object({ name: z.string(), url: z.string() })),
  items: z.array(IntakeItem),
  createdBy: z.string(),
  createdAt: z.number(),
  /** Until when Undo closes the tickets it made; null once it can no longer. */
  undoUntil: z.number().nullable(),
})
export type IntakeRun = z.infer<typeof IntakeRun>
export const IntakeRunList = z.object({ runs: z.array(IntakeRun) })
export type IntakeRunList = z.infer<typeof IntakeRunList>
export const IssueList = z.object({ issues: z.array(Issue), lastSyncedAt: z.number().nullable() })
export type IssueList = z.infer<typeof IssueList>


// ── Overview, activity, usage, alerts ──────────────────────────────────────

export const Alert = z.object({
  id: z.string(),
  rule: z.string(),
  subject: z.string(),
  level: z.enum(['down', 'attention']),
  what: z.string(),
  todo: z.string(),
  openedAt: z.number(),
  clearedAt: z.number().nullable(),
})
export type Alert = z.infer<typeof Alert>
export const AlertList = z.object({ alerts: z.array(Alert) })
export type AlertList = z.infer<typeof AlertList>

export const Overview = z.object({
  projects: z.array(z.object({
    key: z.string(),
    name: z.string(),
    repoState: z.enum(['ok', 'missing']),
    /** Our own project, not a client's: listed after the client ones. */
    internal: z.boolean(),
    counts: StageCounts,
    agents: z.number().int(),
    queued: z.number().int(),
  })),
  alerts: z.array(Alert),
  capacity: z.object({ running: z.number().int(), slots: z.number().int(), queued: z.number().int() }),
  claudeLimit: z.object({ limited: z.boolean(), resetsAt: z.number().nullable() }),
})
export type Overview = z.infer<typeof Overview>

/** Home: one card per project the reader can see, with its situation at a glance. */
export const CardTicket = z.object({ identifier: z.string(), title: z.string(), url: z.string(), priority: z.number().int().nullable(), estimate: z.number().nullable(), assignee: z.string().nullable(), assigneeAvatar: z.string().nullable() })
export type CardTicket = z.infer<typeof CardTicket>
export const ProjectCard = z.object({
  key: z.string(),
  name: z.string(),
  stage: z.string(),
  repoState: z.enum(['ok', 'missing']),
  internal: z.boolean(),
  /** Jobs running on a box now, and waiting for one. */
  running: z.number().int(),
  queued: z.number().int(),
  needsYou: z.number().int(),
  prsOpen: z.number().int(),
  merged7d: z.number().int(),
  declined: z.number().int(),
  alerts: z.number().int(),
  alertLevel: z.enum(['down', 'attention']).nullable(),
  /** 'ready' when any container is; else the worst state seen; null when the project has none. */
  container: z.enum(['ready', 'starting', 'draining', 'stopped', 'failed']).nullable(),
  lastActivityAt: z.number().nullable(),
  /** Tickets in a status that wants someone, for the counts and their hover lists (capped at 50 each). */
  tickets: z.object({ attention: z.array(CardTicket), blocked: z.array(CardTicket), client: z.array(CardTicket) }),
  /** The team's current Linear cycle: how many of its tickets are done. */
  cycle: z.object({ number: z.number().int(), name: z.string().nullable(), startsAt: z.number(), endsAt: z.number(), total: z.number().int(), done: z.number().int() }).nullable(),
  /** When the last ship to production finished. */
  lastShipAt: z.number().nullable(),
  /** Of the PRs merged in 30 days, how many needed a fix run first. */
  rework: z.object({ merged: z.number().int(), reworked: z.number().int() }),
})
export type ProjectCard = z.infer<typeof ProjectCard>
export const ProjectCards = z.object({ projects: z.array(ProjectCard) })
export type ProjectCards = z.infer<typeof ProjectCards>

/** What on one project wants a person: tickets waiting on someone, and open alerts. */
export const ProjectAttention = z.object({
  needsYou: z.array(z.object({
    workspaceId: z.string(),
    ticket: z.string(),
    title: z.string(),
    linearUrl: z.string(),
    reason: z.string().nullable(),
    /** The reason in one plain line (services/plain.ts). */
    needed: z.string().nullable(),
    /** The last finished job, which Retry starts again. */
    lastJobId: z.string().nullable(),
    updatedAt: z.number(),
    /** Claude asked a question (`needed` is it), rather than stopped. */
    asks: z.boolean().optional(),
    /** Pictures Claude made with its last run (diagrams, mockups). */
    pictures: z.number().int().optional(),
  })),
  alerts: z.array(Alert),
})
export type ProjectAttention = z.infer<typeof ProjectAttention>

export const UsageQuery = z.object({
  project: z.string().optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  group: z.enum(['day', 'project', 'model']).default('day'),
})
export type UsageQuery = z.infer<typeof UsageQuery>
export const UsageRow = z.object({
  key: z.string(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  cacheRead: z.number(),
  cacheWrite: z.number(),
  sessions: z.number().int(),
})
export type UsageRow = z.infer<typeof UsageRow>
export const UsageResponse = z.object({ group: z.enum(['day', 'project', 'model']), rows: z.array(UsageRow) })
export type UsageResponse = z.infer<typeof UsageResponse>

/**
 * How full the subscription windows are. An ESTIMATE: Anthropic publishes no
 * ceiling, so `ceiling` is the median of what the window held at recent limit
 * hits, and is null until the first hit has been seen.
 */
export const UsageWindow = z.object({
  used: z.number(),
  ceiling: z.number().nullable(),
  percent: z.number().nullable(),
  samples: z.number().int(),
  /** When the window resets, if Anthropic said (account windows only). */
  resetsAt: z.number().nullable().optional(),
})
export type UsageWindow = z.infer<typeof UsageWindow>
export const UsageSummaryQuery = z.object({
  project: z.string().optional(),
  days: z.coerce.number().int().min(1).max(90).default(7),
})
export type UsageSummaryQuery = z.infer<typeof UsageSummaryQuery>
export const UsageTicket = z.object({
  projectKey: z.string(),
  ticket: z.string(),
  title: z.string(),
  tokens: z.number(),
  sessions: z.number().int(),
})
export type UsageTicket = z.infer<typeof UsageTicket>
/** One subscription's windows. `accountId` null: the project-secret login used before accounts (or when there are none). */
export const AccountUsage = z.object({
  accountId: z.string().nullable(),
  label: z.string(),
  state: z.enum(['ok', 'limited', 'invalid']),
  resetsAt: z.number().nullable(),
  windows: z.object({ fiveHour: UsageWindow, week: UsageWindow }),
  lastHitAt: z.number().nullable(),
})
export type AccountUsage = z.infer<typeof AccountUsage>
export const UsageSummary = z.object({
  /** Per Claude account, whatever project filter is applied: a limit is an account's, not a project's. */
  accounts: z.array(AccountUsage),
  topTickets: z.array(UsageTicket),
})
export type UsageSummary = z.infer<typeof UsageSummary>

// ── Claude accounts ─────────────────────────────────────────────────────────

/** A Claude subscription. The token is write-only: after it is stored, only its last four characters come back. */
export const ClaudeAccount = z.object({
  id: z.string(),
  label: z.string(),
  last4: z.string(),
  addedBy: z.string().nullable(),
  addedVia: z.enum(['paste', 'sign_in']),
  createdAt: z.number(),
  state: z.enum(['ok', 'limited', 'invalid']),
  resetsAt: z.number().nullable(),
  stateReason: z.string().nullable(),
  isDefault: z.boolean(),
  lastUsedAt: z.number().nullable(),
  usage: z.object({ fiveHour: UsageWindow, week: UsageWindow }),
  /** Keys of the projects it serves; the default account serves every project and lists none. */
  projects: z.array(z.string()),
  version: z.number().int(),
})
export type ClaudeAccount = z.infer<typeof ClaudeAccount>
export const ClaudeAccountList = z.object({ accounts: z.array(ClaudeAccount) })
export type ClaudeAccountList = z.infer<typeof ClaudeAccountList>
/** What `claude setup-token` prints. An API key (sk-ant-api…) is refused: Claude runs only on subscriptions. */
export const CLAUDE_OAUTH_TOKEN = /^sk-ant-oat01-[A-Za-z0-9_-]{20,300}$/
export const ClaudeAccountLabel = z.string().trim().min(1).max(60)
export const ClaudeAccountCreate = z.object({
  label: ClaudeAccountLabel,
  token: z.string().trim().regex(CLAUDE_OAUTH_TOKEN, 'Not a token. Paste the sk-ant-oat01-… line `claude setup-token` prints at the end, not the sign-in code (use Sign in for that)'),
})
export type ClaudeAccountCreate = z.infer<typeof ClaudeAccountCreate>
export const ClaudeAccountPatch = z.object({
  label: ClaudeAccountLabel.optional(),
  token: ClaudeAccountCreate.shape.token.optional(),
  /** The project keys it serves, replacing the list. Ignored for the default account, which serves all. */
  projects: z.array(z.string().min(1).max(64)).max(500).optional(),
})
export type ClaudeAccountPatch = z.infer<typeof ClaudeAccountPatch>
export const ClaudeLoginCreate = z.object({ label: ClaudeAccountLabel })
export type ClaudeLoginCreate = z.infer<typeof ClaudeLoginCreate>
export const ClaudeLoginCode = z.object({ code: z.string().trim().min(4).max(512).regex(/^[A-Za-z0-9_#.~-]+$/, 'Paste the code exactly as the sign-in page shows it') })
export type ClaudeLoginCode = z.infer<typeof ClaudeLoginCode>
/** A sign-in running on a box. `url` appears once the CLI printed it; the token itself is never here. */
export const ClaudeLogin = z.object({
  id: z.string(),
  label: z.string(),
  serverId: z.string(),
  state: z.enum(['starting', 'waiting_code', 'code_sent', 'done', 'failed', 'timeout', 'cancelled']),
  url: z.string().nullable(),
  accountId: z.string().nullable(),
  error: z.string().nullable(),
  createdAt: z.number(),
  expiresAt: z.number(),
})
export type ClaudeLogin = z.infer<typeof ClaudeLogin>
export const WorkspaceAccountPut = z.object({ accountId: z.string().max(64).nullable() })
export type WorkspaceAccountPut = z.infer<typeof WorkspaceAccountPut>

// ── Servers, containers ─────────────────────────────────────────────────────

const MemStepName = z.enum(['install', 'baseline', 'suite', 'dev_server', 'browser_proof'])
/** Memory admission for one container, from the box's last heartbeat (02 §6.3). */
export const ServerMemory = z.object({
  instance: z.string(),
  project: z.string(),
  capMb: z.number(),
  usedMb: z.number(),
  reservedMb: z.number(),
  queued: z.number(),
  running: z.array(z.object({ step: MemStepName, jobId: z.string(), reservedMb: z.number(), usedMb: z.number(), capped: z.boolean() })),
  learned: z.array(z.object({ step: MemStepName, peakMb: z.number(), needMb: z.number(), runs: z.number() })),
})
export type ServerMemory = z.infer<typeof ServerMemory>
/** One `trisof-box doctor` check, as the box last reported it. */
export const ServerCheck = z.object({ id: z.string(), status: z.enum(['pass', 'warn', 'fail']), detail: z.string(), fix: z.string() })
export type ServerCheck = z.infer<typeof ServerCheck>

export const Server = z.object({
  id: z.string(),
  hostname: z.string(),
  state: z.enum(['active', 'draining', 'down', 'retired']),
  agentVersion: z.string().nullable(),
  claudeVersion: z.string().nullable(),
  lastSeenAt: z.number().nullable(),
  lastHealthAt: z.number().nullable(),
  stats: z.object({ memFreeMb: z.number(), diskFreeGb: z.number(), load: z.number(), cpuPct: z.number().nullable().optional() }).nullable(),
  memory: z.array(ServerMemory),
  checks: z.object({ at: z.number(), items: z.array(ServerCheck) }).nullable(),
  cpuCores: z.number().nullable(),
  memMb: z.number().nullable(),
  diskGb: z.number().nullable(),
  /** `agent` runs project containers; `ci` runs pipeline jobs, `ciSlots` at a time. */
  role: z.enum(['agent', 'ci']),
  ciSlots: z.number().int(),
  ...Versioned,
})
export type Server = z.infer<typeof Server>
export const ServerList = z.object({ servers: z.array(Server) })
/** How a box gets in: an admin registers it and gets a one-use code the box types in at first boot. */
export const ServerCreate = z.object({ id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/), hostname: z.string().min(1) })
export type ServerCreate = z.infer<typeof ServerCreate>
export const EnrolCode = z.object({ id: z.string(), hostname: z.string().optional(), enrol_code: z.string(), enrol_expires_at: z.number() })
export type EnrolCode = z.infer<typeof EnrolCode>
export type ServerList = z.infer<typeof ServerList>
export const ServerPatch = z.object({ state: z.enum(['active', 'draining']).optional(), ciSlots: z.number().int().min(0).max(32).optional() })
  .refine((b) => b.state !== undefined || b.ciSlots !== undefined, 'Nothing to change')
export type ServerPatch = z.infer<typeof ServerPatch>

export const Container = z.object({
  id: z.string(),
  projectKey: z.string(),
  serverId: z.string(),
  image: z.string(),
  memMb: z.number().int(),
  state: z.enum(['starting', 'ready', 'draining', 'stopped', 'failed']),
  slots: z.number().int(),
  busy: z.number().int(),
  /** Claude sessions open in it now: ticket jobs and people's sessions both. */
  sessions: z.number().int(),
  /** For If-Match on PATCH. */
  version: z.number().int().optional(),
})
export type Container = z.infer<typeof Container>
export const ContainerList = z.object({ containers: z.array(Container) })
export type ContainerList = z.infer<typeof ContainerList>
/** `force` skips the drain: running work is aborted and retried, never charged an attempt. */
export const ContainerRestart = z.object({ force: z.boolean().optional() })
export type ContainerRestart = z.infer<typeof ContainerRestart>
export const ContainerCreate = z.object({ projectKey: z.string(), serverId: z.string().optional(), memMb: z.number().int().min(1024).max(65536).optional() })
export type ContainerCreate = z.infer<typeof ContainerCreate>
/** What a container's card can change: its memory (recreates it) and how many sessions it takes. */
export const ContainerPatch = z.object({ memMb: z.number().int().min(1024).max(65536).optional(), slots: z.number().int().min(1).max(16).optional() })
export type ContainerPatch = z.infer<typeof ContainerPatch>
/** A CI job a server is running now, to open from its card. */
export const ServerCiJob = z.object({ id: z.string(), name: z.string(), runId: z.string(), projectKey: z.string(), branch: z.string().nullable(), startedAt: z.number().nullable() })
export const ServerCiJobs = z.object({ jobs: z.array(ServerCiJob) })
export type ServerCiJobs = z.infer<typeof ServerCiJobs>
/** The platform's own deploy jobs running now: servers restart their agent while one does. */
export const FleetUpdating = z.object({ jobs: z.array(z.object({ name: z.string(), runId: z.string(), startedAt: z.number().nullable() })) })
export type FleetUpdating = z.infer<typeof FleetUpdating>

// ── Integrations, audit, admin ──────────────────────────────────────────────

/**
 * `secret` is the token, or the whole JSON file for a Google service account.
 * App Store Connect also needs its issuer and key ids next to the .p8 key.
 */
export const ConnectionCreate = z.object({
  provider: ConnectionProvider,
  label: z.string().trim().max(80).optional(),
  secret: z.string().trim().min(8).max(20_000),
  issuerId: z.string().trim().max(100).optional(),
  keyId: z.string().trim().max(100).optional(),
  /** Apple Developer Team ID, so EAS can sign iOS builds without asking. */
  teamId: z.string().trim().max(20).optional(),
})
export type ConnectionCreate = z.infer<typeof ConnectionCreate>

export const Integrations = z.object({
  github: z.object({
    /** False until the GitHub App exists (Connections → Create, or Worker secrets); projects then use a token. */
    appReady: z.boolean(),
    /** The app's GitHub name, once it exists. */
    appSlug: z.string().nullable(),
    installations: z.array(z.object({ id: z.number(), account: z.string(), type: z.string(), suspended: z.boolean() })),
    repos: z.array(RepoInfo.extend({ installationId: z.number().nullable(), linkedProject: z.string().nullable() })),
  }),
  linear: z.object({
    orgs: z.array(z.object({
      id: z.string(),
      name: z.string(),
      guardTripped: z.boolean(),
      guardReason: z.string().nullable(),
      /** ok, or failed when Linear refused the token and a reconnect is needed. */
      auth: z.enum(['ok', 'failed']),
      /** When the current token expires; renewed from an hour before. Null for a token that does not expire. */
      expiresAt: z.number().nullable(),
      teams: z.array(z.object({ id: z.string(), key: z.string(), name: z.string() })),
    })),
  }),
  slack: z.object({
    /** A bot token is set on the Worker. */
    ready: z.boolean(),
    team: z.string().nullable(),
    bot: z.string().nullable(),
    /** What Slack said when the token was checked, if it refused it. */
    error: z.string().nullable(),
  }),
  /** Projects that reach GitHub with a pasted token instead of the App. */
  githubTokens: z.array(z.object({ project: z.string(), repo: z.string(), last4: z.string().nullable(), updatedAt: z.number().nullable() })),
  connections: z.array(Connection),
})
export type Integrations = z.infer<typeof Integrations>
export const InstallUrl = z.object({ url: z.string() })
export type InstallUrl = z.infer<typeof InstallUrl>
/** GitHub's create-from-manifest form: POST `manifest` to `url` (it carries the signed state). */
export const AppManifest = z.object({ url: z.string(), manifest: z.string() })
export type AppManifest = z.infer<typeof AppManifest>

export const AuditEntry = z.object({
  id: z.number().int(),
  at: z.number(),
  actor: z.string(),
  action: z.string(),
  projectId: z.string().nullable(),
  target: z.string().nullable(),
  detail: z.unknown().nullable(),
})
export type AuditEntry = z.infer<typeof AuditEntry>
export const AuditList = z.object({ entries: z.array(AuditEntry) })
export type AuditList = z.infer<typeof AuditList>

export const InboxReplay = z.object({ source: z.enum(['github', 'linear']), since: z.number() })
export type InboxReplay = z.infer<typeof InboxReplay>
export const InboxReplayResponse = z.object({ reset: z.number().int() })
export type InboxReplayResponse = z.infer<typeof InboxReplayResponse>

// ── Live socket (/api/live) ─────────────────────────────────────────────────

export const LiveClientMessage = z.discriminatedUnion('type', [
  z.object({ type: z.literal('subscribe'), topic: z.string() }),
  z.object({ type: z.literal('unsubscribe'), topic: z.string() }),
])
export type LiveClientMessage = z.infer<typeof LiveClientMessage>

/**
 * Server → browser. `topic` is `project:<key>`, `workspace:<id>` or `global`;
 * `seq` allows replay after reconnect. On `global`, an event with
 * `data.projectId` reaches only people who can see that project.
 */
export const LiveEvent = z.object({
  topic: z.string(),
  seq: z.number().int(),
  event: z.enum([
    'workspace.stage', 'job.updated', 'job.phase', 'job.event', 'session.opened', 'session.closed',
    'session.presence', 'container.healed', 'usage.updated', 'alert.opened', 'alert.cleared', 'project.updated', 'checks.updated',
    'message.created', 'message.delivered', 'server.updated', 'container.updated', 'inbox.changed', 'activity.changed',
    'claude.accounts', 'claude.login', 'session.account_switched', 'ci.updated', 'ci.log',
  ]),
  data: z.record(z.unknown()),
})
export type LiveEvent = z.infer<typeof LiveEvent>

/** Route table: path → schemas. The web client is generated against this. */
// ── Scopes (features/03): admin only; a scope lives in the `general` workspace ──

export const SCOPE_STATUSES = ['draft', 'sent', 'won', 'lost'] as const
export const ScopeStatus = z.enum(SCOPE_STATUSES)
export type ScopeStatus = z.infer<typeof ScopeStatus>

export const ScopeSetupStatus = z.object({
  /** The `general` project exists. */
  ready: z.boolean(),
  repo: z.string(),
  container: z.object({ id: z.string(), state: z.string() }).nullable(),
  /** The GitHub App is installed on the scoping repo, so setup needs no token. */
  appCanReach: z.boolean(),
  /** The always-on scoping session, once it is running; Scopes opens straight into it. */
  sessionId: z.string().nullable(),
})
export type ScopeSetupStatus = z.infer<typeof ScopeSetupStatus>

export const ScopeSetup = z.object({
  /** A GitHub token that can push to the scoping repo; stored sealed, never returned. Left out when the App reaches the repo. */
  token: z.string().min(20).max(255).optional(),
  user: z.string().max(100).optional(),
})
export type ScopeSetup = z.infer<typeof ScopeSetup>

export const ScopeSession = z.object({ id: z.string().nullable(), tmuxSession: z.string(), label: z.string().nullable(), live: z.boolean(), createdAt: z.number() })
export type ScopeSession = z.infer<typeof ScopeSession>

export const Scope = z.object({
  id: z.string(),
  client: z.string(),
  title: z.string(),
  slug: z.string(),
  /** Path of the scope form in the scoping repo. */
  path: z.string(),
  brief: z.string(),
  status: ScopeStatus,
  priceGbp: z.number().nullable(),
  projectKey: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
  version: z.number(),
  sessions: z.array(ScopeSession),
})
export type Scope = z.infer<typeof Scope>
export const ScopeList = z.object({ scopes: z.array(Scope) })
export type ScopeList = z.infer<typeof ScopeList>

/** Text files only; their contents go into the brief Claude is given. */
export const ScopeFile = z.object({ name: z.string().min(1).max(200), text: z.string().max(200_000) })
export const ScopeCreate = z.object({
  client: z.string().min(1).max(100),
  title: z.string().min(1).max(150),
  brief: z.string().min(1).max(50_000),
  files: z.array(ScopeFile).max(10).default([]),
  accountId: z.string().optional(),
})
export type ScopeCreate = z.infer<typeof ScopeCreate>

export const ScopePatch = z.object({
  status: ScopeStatus.optional(),
  priceGbp: z.number().int().min(0).nullable().optional(),
  projectKey: z.string().nullable().optional(),
})
export type ScopePatch = z.infer<typeof ScopePatch>

export const ScopeVersion = z.object({ sha: z.string(), message: z.string(), author: z.string().nullable(), at: z.number() })
export type ScopeVersion = z.infer<typeof ScopeVersion>
export const ScopeVersions = z.object({ versions: z.array(ScopeVersion) })
export type ScopeVersions = z.infer<typeof ScopeVersions>
export const ScopeForm = z.object({ sha: z.string().nullable(), text: z.string().nullable(), client: z.boolean() })
export type ScopeForm = z.infer<typeof ScopeForm>

// ── Detailed scope (features/05, 06) ──

export const ProjectScopeApprove = z.object({
  /** The scope as text. Empty with a Google Doc link: the doc is read (it must be shared by link). */
  text: z.string().max(400_000).default(''),
  sourceUrl: z.string().url().max(500).optional(),
})
export type ProjectScopeApprove = z.infer<typeof ProjectScopeApprove>
export const ApprovedScope = z.object({ id: z.string(), sourceUrl: z.string().nullable(), body: z.string(), approvedBy: z.string(), approvedAt: z.number(), points: z.number() })
export type ApprovedScope = z.infer<typeof ApprovedScope>
export const ProjectScopeView = z.object({
  current: ApprovedScope.nullable(),
  history: z.array(ApprovedScope.omit({ body: true })),
  /** Ticket runs made from the current copy. */
  runs: z.array(z.object({ id: z.string(), pass: z.string().nullable(), title: z.string(), status: z.string(), summary: z.string().nullable(), createdAt: z.number() })),
})
export type ProjectScopeView = z.infer<typeof ProjectScopeView>
export const ScopeTicketsStart = z.object({ stage: z.enum(['mvp', 'backend']) })
export type ScopeTicketsStart = z.infer<typeof ScopeTicketsStart>
export const CoverageLine = z.object({
  n: z.number(), text: z.string(), heading: z.boolean(),
  tickets: z.array(z.object({ identifier: z.string(), title: z.string(), url: z.string(), state: z.string(), done: z.boolean() })),
})
export const ScopeCoverage = z.object({
  scopeId: z.string().nullable(),
  lines: z.array(CoverageLine),
  /** Points (non-heading lines) with any ticket, and with every ticket done. */
  points: z.number(), covered: z.number(), done: z.number(),
})
export type ScopeCoverage = z.infer<typeof ScopeCoverage>

// ── Feedback hub (features/14) ─────────────────────────────────────────────

/** What the widget sends. The screenshot is a PNG or JPEG data url. */
export const FeedbackSubmit = z.object({
  /** Chosen by the widget, so a resend after a dropped answer makes no second ticket. */
  id: z.string().uuid(),
  note: z.string().trim().min(1).max(5000),
  page: z.string().max(2000).optional(),
  browser: z.string().max(500).optional(),
  user: z.string().max(200).optional(),
  area: z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() }).optional(),
  screenshot: z.string().max(7_000_000).regex(/^data:image\/(png|jpeg);base64,/).optional(),
})
export type FeedbackSubmit = z.infer<typeof FeedbackSubmit>
export const FeedbackItem = z.object({
  id: z.string(), note: z.string(), page: z.string().nullable(), browser: z.string().nullable(), user: z.string().nullable(),
  area: z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() }).nullable(),
  screenshotUrl: z.string().nullable(), ticket: z.object({ identifier: z.string(), url: z.string(), state: z.string().nullable() }).nullable(), createdAt: z.number(),
})
export type FeedbackItem = z.infer<typeof FeedbackItem>
export const FeedbackList = z.object({
  /** The snippet for the UAT and production apps. Every project has one (features/14). */
  key: z.string().nullable(), snippet: z.string().nullable(), open: z.boolean(), items: z.array(FeedbackItem),
  /** Made from main-template: the button is built in and reads its key from the GitHub Environments, so no snippet. */
  builtIn: z.boolean(),
})
export type FeedbackList = z.infer<typeof FeedbackList>

// ── Pricing from real hours (features/03) ──────────────────────────────────

/** What the platform measured on the delivered project, to sit beside the quote. */
export const ActualFacts = z.object({
  projectKey: z.string(), quotedGbp: z.number().nullable(), weeks: z.number().nullable(),
  ticketsDone: z.number(), ticketsByKind: z.record(z.string(), z.number()),
  robotHours: z.number(), sessionHours: z.number(), delivered: z.boolean(),
})
export type ActualFacts = z.infer<typeof ActualFacts>
export const ScopeActuals = z.object({
  facts: ActualFacts.nullable(),
  saved: z.object({ hours: z.number(), notes: z.string(), recordedBy: z.string(), recordedAt: z.number(), path: z.string() }).nullable(),
})
export type ScopeActuals = z.infer<typeof ScopeActuals>
export const ScopeActualsPut = z.object({ hours: z.number().positive().max(100_000), notes: z.string().max(10_000).default('') })
export type ScopeActualsPut = z.infer<typeof ScopeActualsPut>

// ── Dev account links (features/17) ───────────────────────────────────────

export const AccountLinks = z.object({
  githubLogin: z.string().nullable(),
  /** The GitHub account your sessions commit as (features/25); null until you press Link GitHub. */
  githubLinked: z.string().nullable(),
  /** Whether Link GitHub can work yet (the App's OAuth secret is set). */
  githubReady: z.boolean(),
  linearUserId: z.string().nullable(),
  slackUserId: z.string().nullable(),
  /** Linear people not yet linked to anyone, plus your own: the choices. */
  linearUsers: z.array(z.object({ id: z.string(), name: z.string(), email: z.string().nullable() })),
  slackReady: z.boolean(),
})
export type AccountLinks = z.infer<typeof AccountLinks>
export const AccountLinksPut = z.object({
  githubLogin: z.string().regex(/^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})$/).nullable(),
  linearUserId: z.string().min(1).max(100).nullable(),
  slackUserId: z.string().regex(/^[UW][A-Z0-9]{6,20}$/).nullable(),
})
export type AccountLinksPut = z.infer<typeof AccountLinksPut>

export const ROUTES = {
  'POST /auth/login': { body: LoginRequest, res: LoginResponse },
  'POST /auth/2fa/totp/verify': { body: TotpCode, res: Me },
  'POST /auth/2fa/passkey/options': { body: z.object({}).optional(), res: PasskeyOptionsResponse },
  'POST /auth/2fa/passkey/verify': { body: PasskeyVerifyRequest, res: Me },
  'POST /auth/2fa/recovery': { body: RecoveryCodeRequest, res: Me },
  'POST /auth/2fa/totp/enroll': { body: z.object({}).optional(), res: TotpEnrollResponse },
  'POST /auth/2fa/totp/confirm': { body: TotpCode, res: EnrollConfirmResponse },
  'POST /auth/2fa/passkey/register/options': { body: z.object({}).optional(), res: PasskeyOptionsResponse },
  'POST /auth/2fa/passkey/register/verify': { body: PasskeyVerifyRequest, res: EnrollConfirmResponse },
  'POST /auth/logout': { body: z.object({}).optional(), res: Ok },
  'POST /auth/forgot': { body: ForgotRequest, res: Ok },
  'POST /auth/reset': { body: ResetRequest, res: Ok },
  'POST /auth/invite/accept': { body: InviteAcceptRequest, res: LoginResponse },
  'GET /auth/me': { res: Me },
  'GET /api/account': { res: Account },
  'PATCH /api/account': { body: AccountPatch, res: Account },
  'POST /api/account/password': { body: PasswordChange, res: Ok },
  'DELETE /api/account/passkeys/:id': { res: Ok },
  'DELETE /api/account/totp': { res: Ok },
  'POST /api/account/recovery-codes': { res: RecoveryCodesResponse },
  'GET /api/account/sessions': { res: SessionList },
  'DELETE /api/account/sessions/:id': { res: Ok },
  'GET /api/people': { res: PeopleList },
  'POST /api/people/invite': { body: InviteRequest, res: Ok },
  'PATCH /api/people/:id': { body: PersonPatch, res: Person },
  'POST /api/people/:id/reset-2fa': { res: Ok },
  'PUT /api/projects/:key/access/:personId': { body: AccessPut, res: Ok },
  'DELETE /api/projects/:key/access/:personId': { res: Ok },
  'GET /api/overview': { res: Overview },
  'GET /api/projects': { res: ProjectList },
  'POST /api/projects': { body: ProjectCreate, res: Project },
  'GET /api/projects/:key': { res: Project },
  'PATCH /api/projects/:key': { body: ProjectPatch, res: Project },
  'POST /api/projects/:key/archive': { res: Ok },
  'GET /api/projects/:key/secrets': { res: SecretList },
  'PUT /api/projects/:key/secrets/:name': { body: SecretPut, res: SecretMeta },
  'DELETE /api/projects/:key/secrets/:name': { res: Ok },
  'POST /api/projects/:key/repo': { body: RepoReconnect, res: Project },
  'GET /api/projects/:key/stages': { res: ProjectStages },
  'POST /api/projects/:key/stages/check': { body: StageCheckSet, res: ProjectStages },
  'POST /api/projects/:key/stages/advance': { body: StageAdvance, res: ProjectStages },
  'POST /api/projects/:key/issues/:identifier/triage': { body: TriageDecide, res: TicketTriage },
  'PUT /api/projects/:key/issues/:identifier/levels': { body: LevelsSet, res: LevelsSetResult },
  'POST /api/projects/:key/issues/start': { body: BatchStart, res: BatchStarted },
  'POST /api/projects/:key/intake/files': { res: IntakeFile },
  'POST /api/projects/:key/intake': { body: IntakeStart, res: IntakeRun },
  'GET /api/projects/:key/intake': { res: IntakeRunList },
  'GET /api/projects/:key/scope': { res: ProjectScopeView },
  'POST /api/projects/:key/scope': { body: ProjectScopeApprove, res: ProjectScopeView },
  'POST /api/projects/:key/scope/tickets': { body: ScopeTicketsStart, res: ProjectScopeView },
  'GET /api/projects/:key/scope/coverage': { res: ScopeCoverage },
  'GET /api/projects/:key/feedback': { res: FeedbackList },
  'GET /api/scopes/:id/actuals': { res: ScopeActuals },
  'GET /api/account/links': { res: AccountLinks },
  'PUT /api/account/links': { body: AccountLinksPut, res: AccountLinks },
  'POST /api/scopes/:id/actuals': { body: ScopeActualsPut, res: ScopeActuals },
  'POST /api/projects/:key/feedback/key': { body: z.object({}).optional(), res: FeedbackList },
  'GET /api/scopes/setup': { res: ScopeSetupStatus },
  'POST /api/scopes/setup': { body: ScopeSetup, res: ScopeSetupStatus },
  'GET /api/scopes': { res: ScopeList },
  'POST /api/scopes': { body: ScopeCreate, res: Scope },
  'GET /api/scopes/:id': { res: Scope },
  'PATCH /api/scopes/:id': { body: ScopePatch, res: Scope },
  'POST /api/scopes/:id/sessions': { res: Scope },
  'GET /api/scopes/:id/versions': { res: ScopeVersions },
  'GET /api/scopes/:id/form': { res: ScopeForm },
  'GET /api/projects/:key/intake/:id': { res: IntakeRun },
  'POST /api/projects/:key/intake/:id/undo': { res: IntakeRun },
  'GET /api/projects/:key/board': { res: Board },
  'GET /api/projects/:key/issues': { res: IssueList },
  'POST /api/projects/:key/pause': { res: Project },
  'POST /api/projects/:key/resume': { res: Project },
  'GET /api/projects/:key/workspaces/:ticket': { res: Workspace },
  'POST /api/projects/:key/issues/:identifier/delegate': { body: JobCreate.optional(), res: JobCreated },
  'GET /api/invites': { res: InviteList },
  'POST /api/invites/:id/resend': { res: Ok },
  'DELETE /api/invites/:id': { res: Ok },
  'GET /api/workspaces/:id': { res: Workspace },
  'GET /api/workspaces/:id/timeline': { res: Timeline },
  'GET /api/workspaces/:id/diff': { res: Diff },
  'GET /api/workspaces/:id/checks': { res: Checks },
  'GET /api/workspaces/:id/sessions': { res: SessionsList },
  'POST /api/workspaces/:id/jobs': { body: JobCreate, res: JobCreated },
  'POST /api/jobs/:id/cancel': { res: Ok },
  'POST /api/jobs/:id/retry': { res: JobCreated },
  'POST /api/workspaces/:id/sessions': { body: SessionCreate, res: SessionCreated },
  'DELETE /api/workspaces/:id/sessions/:sid': { res: Ok },
  'POST /api/workspaces/:id/message': { body: MessageSend, res: WorkspaceMessage },
  'GET /api/workspaces/:id/messages': { res: WorkspaceMessageList },
  'POST /api/boxes/token': { body: BoxTokenRequest, res: BoxTokenResponse },
  'GET /api/home': { res: ProjectCards },
  'GET /api/usage': { query: UsageQuery, res: UsageResponse },
  'GET /api/usage/summary': { query: UsageSummaryQuery, res: UsageSummary },
  'GET /api/alerts': { res: AlertList },
  'POST /api/alerts/:id/dismiss': { res: Ok },
  'GET /api/claude-accounts': { res: ClaudeAccountList },
  'POST /api/claude-accounts': { body: ClaudeAccountCreate, res: ClaudeAccount },
  'PATCH /api/claude-accounts/:id': { body: ClaudeAccountPatch, res: ClaudeAccount },
  'POST /api/claude-accounts/:id/default': { res: ClaudeAccount },
  'DELETE /api/claude-accounts/:id': { res: Ok },
  'POST /api/claude-accounts/logins': { body: ClaudeLoginCreate, res: ClaudeLogin },
  'GET /api/claude-accounts/logins/:id': { res: ClaudeLogin },
  'POST /api/claude-accounts/logins/:id/code': { body: ClaudeLoginCode, res: ClaudeLogin },
  'POST /api/claude-accounts/logins/:id/cancel': { res: ClaudeLogin },
  'PUT /api/workspaces/:id/account': { body: WorkspaceAccountPut, res: Workspace },
  'GET /api/servers': { res: ServerList },
  'PATCH /api/servers/:id': { body: ServerPatch, res: Server },
  'POST /api/servers': { body: ServerCreate, res: EnrolCode },
  'POST /api/servers/:id/enrol-code': { body: z.object({}).optional(), res: EnrolCode },
  'GET /api/containers': { res: ContainerList },
  'POST /api/containers': { body: ContainerCreate, res: Container },
  'POST /api/containers/:id/drain': { res: Ok },
  'PATCH /api/containers/:id': { body: ContainerPatch, res: Ok },
  'GET /api/servers/:id/ci-jobs': { res: ServerCiJobs },
  'GET /api/fleet/updating': { res: FleetUpdating },
  'POST /api/containers/:id/restart': { body: ContainerRestart, res: Ok },
  'GET /api/integrations': { res: Integrations },
  'GET /api/integrations/github/install': { res: InstallUrl },
  'GET /api/integrations/github/manifest': { res: AppManifest },
  'POST /api/integrations/github/sync': { res: z.object({ installations: z.number() }) },
  'POST /api/integrations/connections': { body: ConnectionCreate, res: Connection },
  'POST /api/integrations/connections/:id/check': { res: Connection },
  'DELETE /api/integrations/connections/:id': { res: Ok },
  'POST /api/integrations/connections/:id/default': { res: Ok },
  'GET /api/projects/:key/services': { res: ProjectServices },
  'GET /api/project-setups': { res: ProjectSetupList },
  'GET /api/project-setups/options': { res: SetupOptions },
  'POST /api/project-setups': { body: ProjectSetupCreate, res: ProjectSetup },
  'POST /api/project-setups/template': { res: Ok },
  'GET /api/project-setups/:id': { res: ProjectSetup },
  'POST /api/project-setups/:id/steps/:step/retry': { res: ProjectSetup },
  'POST /api/project-setups/:id/steps/:step/done': { res: ProjectSetup },
  'PUT /api/projects/:key/services': { body: ProjectServicesConfig, res: ProjectServices },
  'GET /api/integrations/linear/install': { res: InstallUrl },
  'POST /api/integrations/linear/:org/guard/reset': { res: Ok },
  'GET /api/audit': { res: AuditList },
  'POST /api/admin/inbox/replay': { body: InboxReplay, res: InboxReplayResponse },
} as const

export type RouteKey = keyof typeof ROUTES


export * from './project-file.js'
